import {
  Descriptions,
  Table,
  Tag,
  Tooltip,
  Typography,
  Input,
  InputNumber,
  Button,
  Space,
} from "antd";
import React, { useState, useRef } from "react";
import { SearchOutlined } from "@ant-design/icons";
import moment from "moment";

const StockSelection = (props) => {
  const [selectedRowKeys, setSelectedRowKeys] = useState(props.selectedIds);
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [selectedStocks, setSelectedStocks] = useState([]);
  const searchInput = useRef(null);

  const { Text } = Typography;

  const onSelectChange = (rowKeys) => {
    const previouslySelectedKeys = selectedRowKeys;
    const newlySelectedKeys = rowKeys;

    const uncheckedKeys = previouslySelectedKeys.filter(
      (key) => !newlySelectedKeys.includes(key)
    );

    const updatedStocks = props.stocks.map((item) => {
      if (newlySelectedKeys.includes(item.id)) {
        return { ...item, assignQuantity: item.totalItems };
      } else if (uncheckedKeys.includes(item.id)) {
        return { ...item, assignQuantity: 0 };
      }
      return item;
    });

    setSelectedRowKeys(newlySelectedKeys);
    /* const selectedItems = props.stocks.filter((item) =>
      newlySelectedKeys.includes(item.id)
    ); */
    setSelectedStocks(updatedStocks.filter((item) => newlySelectedKeys.includes(item.id)));
    props.onSelectChange(newlySelectedKeys);
    props.onQuantityChange(updatedStocks);
  };

  const rowSelection = {
    selectedRowKeys,
    onChange: onSelectChange,
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: "block" }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size="small"
            style={{ width: 90 }}
          >
            Reset
          </Button>
          <Button
            type="link"
            size="small"
            onClick={() => {
              confirm({ closeDropdown: false });
              setSearchText(selectedKeys[0]);
              setSearchedColumn(dataIndex);
            }}
          >
            Filter
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1890ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? dataIndex === "runningNo"
          ? record[dataIndex].toString().toLowerCase() === value.toLowerCase()
          : record[dataIndex]
              .toString()
              .toLowerCase()
              .includes(value.toLowerCase())
        : "",
    onFilterDropdownVisibleChange: (visible) => {
      if (visible) {
        setTimeout(
          () =>
            searchInput && searchInput.current && searchInput.current.select(),
          100
        );
      }
    },
    render: (text) => (searchedColumn === dataIndex ? text : text),
  });

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText("");
  };

  const handleQuantityChange = (id, value) => {
    const updatedStocks = props.stocks.map((stock) =>
      stock.id === id
        ? {
            ...stock,
            assignQuantity:
              value > stock.totalItems
                ? stock.totalItems
                : value ?? stock.totalItems,
          }
        : stock
    );
    props.onQuantityChange(updatedStocks);

    const updatedSelectedStocks = updatedStocks.filter((stock) =>
      selectedRowKeys.includes(stock.id)
    );
    setSelectedStocks(updatedSelectedStocks);
  };

  const getColumns = () => {
    let columns = [];
    columns.push({
      title: "Material Type",
      dataIndex: "stockTypeCode",
      key: "stockTypeCode",
      render: (text, record) => {
        return record.stockTypeCode;
      },
    });
    columns.push({
      title: "Description",
      key: "stockTypeName",
      ellipsis: {
        showTitle: false,
      },
      render: (text, record) => {
        return (
          <Tooltip placeholder="topLeft" title={record.stockTypeName}>
            {record.stockTypeName}
          </Tooltip>
        );
      },
    });
    columns.push({
      title: "Receipt Number",
      key: "receiptNumber",
      ...getColumnSearchProps("receiptNumber"),
      render: (text, record) => {
        return record.receiptNumber;
      },
    });
    columns.push({
      title: "Pallet No",
      dataIndex: "palletNo",
      key: "palletNo",
    });
    columns.push({
      title: "Stock Bin",
      dataIndex: "stockBinCode",
      key: "stockBinCode",
      render: (text, record) => {
        return record.stockBinCode;
      },
    });
    columns.push({
      title: "Receive Date",
      dataIndex: "receivedDate",
      key: "receivedDate",
      render: (text, record) => {
        return (
          record.receivedDate &&
          moment(record.receivedDate).format("YYYY-MM-DD")
        );
      },
    });
    columns.push({
      title: "Status",
      dataIndex: "mode",
      key: "mode",
      width: "8%",
      render: (text) => {
        let color = "";
        if (text === "AVAILABLE") {
          color = "#87d068";
        }
        if (text === "ALLOCATED") {
          color = "#2db7f5";
        }
        if (text === "REQUESTED") {
          color = "#2db7f5";
        }
        if (text === "ISSUED") {
          color = "#108ee9";
        }
        if (text === "DELIVERED") {
          color = "#f50";
        }
        if (text === "READY") {
          color = "#faad14";
        }
        if (text === "LOADED") {
          color = "#ffd666";
        }
        return <Tag color={color}>{text}</Tag>;
      },
    });
    columns.push({
      title: "Quantity",
      dataIndex: "totalItems",
      key: "totalItems",
      render: (text, record) => {
        return text && Number(text);
      },
    });
    columns.push({
      title: "Assign Quantity",
      dataIndex: "assignQuantity",
      key: "assignQuantity",
      render: (text, record) => (
        <InputNumber
          min={0}
          max={record.totalItems}
          value={record.assignQuantity}
          onChange={(value) => handleQuantityChange(record.id, value)}
          disabled={true}
        />
      ),
    });

    return columns;
  };

  return (
    <div>
      <Table
        bordered
        rowKey="id"
        size="small"
        columns={getColumns()}
        dataSource={props.stocks}
        pagination={{
          showSizeChanger: true,
          showTotal: (total) => `Total ${total} items`,
        }}
        rowSelection={rowSelection}
        summary={(pageData) => {
          let totalQuantity = 0;
          let totalAssignQuantity = 0;

          pageData.forEach(({ totalItems, assignQuantity }) => {
            totalQuantity += totalItems;
            totalAssignQuantity += assignQuantity;
          });

          return (
            <>
              <Table.Summary.Row>
                <Table.Summary.Cell colSpan={7}>
                  <Text strong>Total</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell>
                  <Text strong style={{ paddingLeft: 12 }}>
                    {props.stocks && props.stocks.length}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell>
                  <Text strong style={{ paddingLeft: 12 }}>
                    {totalQuantity}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell>
                  <Text strong style={{ paddingLeft: 12 }}>
                    {totalAssignQuantity}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </>
          );
        }}
      />
      {selectedRowKeys.length > 0 && (
        <Descriptions title="" bordered style={{ marginTop: 15 }} size="small">
          <Descriptions.Item
            label="Material Type"
            span={3}
            contentStyle={{ fontWeight: "bold" }}
            labelStyle={{ fontWeight: "bold" }}
          >
            {selectedStocks.length > 0 && selectedStocks[0].stockTypeCode}
          </Descriptions.Item>
          <Descriptions.Item
            label="Material Description"
            span={3}
            contentStyle={{ fontWeight: "bold" }}
            labelStyle={{ fontWeight: "bold" }}
          >
            {selectedStocks.length > 0 && selectedStocks[0].stockTypeName}
          </Descriptions.Item>
          <Descriptions.Item
            label="# of Pallets"
            span={3}
            contentStyle={{ fontWeight: "bold" }}
            labelStyle={{ fontWeight: "bold" }}
          >
            {selectedStocks.length}
          </Descriptions.Item>
          <Descriptions.Item
            label="Total Assign Quantity"
            span={3}
            contentStyle={{ fontWeight: "bold" }}
            labelStyle={{ fontWeight: "bold" }}
          >
            {selectedStocks.length > 0 &&
              Number(
                selectedStocks
                  .map((item) => item.assignQuantity)
                  .reduce((prev, next) => prev + next)
              )}
          </Descriptions.Item>
        </Descriptions>
      )}
    </div>
  );
};

export default StockSelection;
